import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { company, services } from "@/data/company";
import { submitInquiry } from "@/lib/apiClient";

const empty = { name: "", email: "", company: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitInquiry(form);
      setForm(empty);
      navigate("/thank-you", {
        state: { ref: res.data?.ref, name: res.data?.name },
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12"
    >
      <div className="grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#22d3ee] font-bold mb-4">
            Get in touch
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            Let&apos;s build something worth talking about.
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed">
            Tell us about your project and goals. We&apos;ll get back to you within
            one business day with next steps.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`mailto:${company.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 text-slate-300 hover:text-[#22d3ee] transition-colors"
            >
              <span className="w-11 h-11 flex items-center justify-center border border-white/10 rounded-sm text-[#22d3ee]">
                <Mail size={18} />
              </span>
              {company.email}
            </a>
            <a
              href={`tel:${company.phone}`}
              className="flex items-center gap-4 text-slate-300 hover:text-[#22d3ee] transition-colors"
            >
              <span className="w-11 h-11 flex items-center justify-center border border-white/10 rounded-sm text-[#22d3ee]">
                <Phone size={18} />
              </span>
              {company.phone}
            </a>
            <div className="flex items-center gap-4 text-slate-300">
              <span className="w-11 h-11 flex items-center justify-center border border-white/10 rounded-sm text-[#22d3ee]">
                <MapPin size={18} />
              </span>
              {company.location}
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-[#10182b] border border-white/10 rounded-sm p-8 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">
                Name *
              </Label>
              <Input
                id="name"
                data-testid="contact-name-input"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Jane Doe"
                className="bg-[#0a0f1e] border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-email-input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="jane@company.com"
                className="bg-[#0a0f1e] border-white/10 text-white"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="company" className="text-slate-300">
                Company
              </Label>
              <Input
                id="company"
                data-testid="contact-company-input"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                placeholder="Company Inc."
                className="bg-[#0a0f1e] border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Service</Label>
              <Select
                value={form.service}
                onValueChange={(v) => update("service", v)}
              >
                <SelectTrigger
                  data-testid="contact-service-select"
                  className="bg-[#0a0f1e] border-white/10 text-white"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.key} value={s.title}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-slate-300">
              Message *
            </Label>
            <Textarea
              id="message"
              data-testid="contact-message-input"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Tell us about your project..."
              rows={5}
              className="bg-[#0a0f1e] border-white/10 text-white resize-none"
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#22d3ee] text-[#0a0f1e] font-bold px-8 py-4 rounded-sm hover:bg-[#67e8f9] transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Sending...
              </>
            ) : (
              <>
                Send message
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
